import React, { useCallback, useMemo, useState } from 'react';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Icon, Input, Label, Select } from '@atlas/ui';

import { PIX_TYPES } from 'common/constants';
import PaymentListItem from 'components/ClientProfile/Payment/PaymentListItem';

import { getInputMode, validatePixKey } from './utils';

const optionsPixtype = Object.values(PIX_TYPES)
  .filter(i => i !== 'evp')
  .map(item => ({
    text: `withdrawal_pixstarspay_pixtype_${item}`,
    value: item,
  }));

export const WithdrawalPixNoEvpPopup = ({ data, id }) => {
  const { formAction, isNewImplementation, paymentMethod, pmId, redirectUrl } = data;
  const parsedData = useMemo(
    () => parseWithdrawalQuery(redirectUrl, { parseNumbers: false }),
    [redirectUrl],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [pixType, setPixType] = useState();
  const [pixKey, setPixKey] = useState('');
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const handleChangePixType = useCallback(
    value => {
      setPixType(value);

      let newPixKey = parsedData[value] || '';

      if (value === PIX_TYPES.PHONE) newPixKey = `+${newPixKey.trim()}`;

      setError(null);
      setPixKey(newPixKey);
    },
    [parsedData],
  );

  const handleChangePixKey = useCallback(
    e => {
      let value = e.target.value.trim();

      if (pixType === PIX_TYPES.PHONE) value = value ? `+${value.replace(/\D/g, '')}` : '';

      const error = validatePixKey(pixType, value);

      setError(error);
      setPixKey(value);
    },
    [pixType],
  );

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;
    const request = prepareRequest({
      method: 'POST',
      url: formAction,
    });

    const { amount, currency, paymentId } = parsedData;

    const requestData = {
      amount,
      currency,
      ...(!isNewImplementation
        ? {
            paymentId,
            pixKey,
            pixType,
          }
        : {
            additionalParameters: {
              pixKey,
              pixKeyType: pixType,
              publicPaymentId: paymentId,
            },
            pmId,
          }),
    };

    setIsSubmitting(true);

    try {
      const response = await request(requestData);
      NotificationManager.display({
        data: {
          amount,
          currency,
          publicPaymentId: response?.publicPaymentId,
          paymentMethod,
        },
        message: 'successfulWithdrawal',
        type: 'successfulWithdrawal',
      });
    } catch (error) {
      NotificationManager.display({
        data: {
          errorType: true,
          headerTitle: 'withdrawal_by_pix',
          text: 'something_went_wrong_contact_support',
          title: 'failed_sad_emoticon',
        },
        type: 'withdrawalResultPopup',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    formAction,
    parsedData,
    isNewImplementation,
    pixKey,
    pixType,
    pmId,
    paymentMethod,
    handleClose,
  ]);

  return (
    <DefaultPopup
      className="withdrawal-pix"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="withdrawal-pix__content">
        <div className="withdrawal-pix__payment-method">
          <PaymentListItem
            amount={parseInt(parsedData.amount)}
            payment={paymentMethod}
            showAmount
          />
        </div>

        <Select
          active={isActiveSelect}
          className="withdrawal-pix__pixtype-select"
          label="withdrawal_pixstarspay_pixtype"
          onBlur={() => setIsActiveSelect(false)}
          onChange={handleChangePixType}
          onFocus={() => setIsActiveSelect(true)}
          options={optionsPixtype}
          translateOptions
        />

        <Input
          active={isActiveInput}
          disabled={pixType === PIX_TYPES.CPF}
          error={error}
          label={`withdrawal_pixstarspay_pixtype_${pixType}`}
          onBlur={() => setIsActiveInput(false)}
          onChange={handleChangePixKey}
          onFocus={() => setIsActiveInput(true)}
          value={pixKey}
          {...getInputMode(pixType)}
        />

        {error && (
          <div className="help-text-container">
            <Label message={error.message} params={error.params} />
          </div>
        )}

        <div className="withdrawal-pix__description">
          <div className="withdrawal-pix__description-title">
            <Icon className="withdrawal-pix__description-icon" name="info" />
            <Label
              className="withdrawal-pix__description-label"
              message="withdrawal_pix_paybrokers_pop_up_info_title"
            />
          </div>
          <div className="withdrawal-pix__description-info">
            <Label
              className="withdrawal-pix__description-span"
              message={
                pixType === PIX_TYPES.CPF
                  ? 'withdrawal_pix_paybrokers_pop_up_info_cpf'
                  : 'withdrawal_pix_paybrokers_pop_up_info_email_or_phone'
              }
            />
          </div>
        </div>

        <Button
          className="withdrawal-pix__button"
          disabled={!pixKey || error || isSubmitting}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};
